import React, { useState, useEffect } from 'react'
import CommonApi from '@/pages/api'
import { CommentNotifyIcon, DostNotifyIcon, SingleDotIcon } from '@/src/assets/icons'
import { Box, Drawer, Typography } from '@mui/material'
import { ManageNotifications } from '@/pages/api/endpoints'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useRouter } from 'next/router'
import { useStore } from '@/src/types/store'
dayjs.extend(relativeTime)

const Notification = () => {
  const router = useRouter()
  const { profile } = useStore()
  const [notifications, setNotifications] = useState([])
  const [readNotif, setReadNotif] = useState<number[]>([])
  const [openDrawer, setOpenDrawer] = useState<boolean>(false)

  useEffect(() => {
    fetchNotifications()
  }, [])

  const fetchNotifications = async () => {
    try {
      const data = await CommonApi({
        path: ManageNotifications.getList,
        method: 'get'
      })
      setNotifications(data?.data ?? [])
    } catch (error) {
      console.error('Error fetching notifications:', error)
    }
  }

  const toggleNotificationDrawer = () => {
    setOpenDrawer(!openDrawer)
  }

  const handleNotificationClick = async (data: any) => {
    // Webby suggestions are the one type NOT marked read on click. Clicking may
    // land on the single-session lock (another session still unpublished), in
    // which case nothing has actually been actioned — marking it read there
    // would clear the bell and bury a task the admin still has to do. The
    // backend clears it for every recipient once the suggestion is genuinely
    // opened or dismissed (PATCH /webby/suggestions/{id}).
    // Boolean, not the id itself: the `||` would otherwise yield the raw
    // webby_suggestion_id and make the read-marking branch below depend on its
    // truthiness. Real ids are positive serials so it holds today, but the
    // read/unread decision should not rest on that.
    const isWebbySuggestion = Boolean(
      data?.label === 'webby-suggestion' || data?.webby_suggestion_id != null
    )
    try {
      if (!data.read && !isWebbySuggestion) {
        const resp = await CommonApi({
          path: ManageNotifications.update(data.id),
          method: 'patch',
          data: { ...data, read: true }
        })
        if (resp.status) {
          setReadNotif([...readNotif, data.id])
        }
      }
    } catch (err) {
      console.error('Error updating notifications:', err)
    }
    if (isWebbySuggestion) {
      // Webby found something in the latest announcement that can update the
      // site — deep-link straight into the Webby editor with the suggestion
      // id so IRHQWebbyChat can prefill the chatbox (see F3).
      //
      // Guard the id: template-interpolating a missing one yields the STRING
      // "undefined", which is truthy and passes a typeof check downstream, so
      // it reaches the API as /webby/suggestions/undefined and 400s with no
      // visible error. Land on the editor without the deep-link instead — the
      // notification stays unread, so nothing is lost.
      const suggestionId = data?.webby_suggestion_id
      router.push(
        suggestionId != null
          ? `/settings/webby?suggestion=${encodeURIComponent(String(suggestionId))}`
          : '/settings/webby'
      )
    } else if (
      data?.heading === 'Successfully Connected to Shareholders' ||
      data?.heading === 'Connecting to Shareholders' ||
      data?.heading === 'Investor Access Restricted'
    ) {
      if (data?.user_type == 'investor') {
        router.push(`/manage-investors?investor_id=${data?.by_id}`)
      } else {
        router.push(`/manage-admin?admin_id=${btoa(JSON.stringify(data?.by_id))}`)
      }
    } else if (data?.content_id) {
      router.push(`/manage-content?preview_id=${data?.content_id}`)
    } else if (data?.label.toLowerCase().includes('question')) {
      const id = data.question_id?.toString()
      if (profile.user_type === 'investor') {
        router.push('/my-questions')
      } else {
        router.push(`/${data.label}?id=${id}`)
      }
    } else if (data?.i_email_id) {
      router.push(`/email-enquiries?id=${data?.i_email_id}`)
    }
  }

  return (
    <>
      <div
        className="cursor-pointer"
        data-testid="automation-IRHQ_Header-NotifyIcon"
        onClick={toggleNotificationDrawer}
      >
        <Box className="relative">
          {notifications?.filter((d: any) => !(d.read || readNotif.includes(d.id))).length > 0 && (
            <Box className="bg-red-700 w-2 h-2 rounded-full absolute right-0 top-[1px]"></Box>
          )}
          <DostNotifyIcon />
        </Box>
      </div>
      <div id="navBarDrawer">
        <Drawer
          id="navBarDrawer"
          open={openDrawer}
          className="flex flex-col gap-4"
          onClose={() => setOpenDrawer(false)}
          anchor="right"
          classes={{
            paper: 'h-[calc(100vh_-_65px)] mt-[65px]'
          }}
        >
          <div className=" w-[21.688rem] flex flex-col gap-y-2 my-2">
            {notifications?.length > 0 ? (
              notifications.map((notification: any, index) => (
                <div
                  key={index}
                  className={`flex hover:bg-[#D0E8F5] justify-between items-top gap-2 cursor-pointer px-4 py-2 ${
                    notification.read || readNotif.includes(notification.id)
                      ? ''
                      : '!bg-status-notificationBack'
                  }`}
                  onClick={handleNotificationClick.bind(this, notification)}
                >
                  <div className="flex justify-start items-top gap-x-4">
                    <CommentNotifyIcon className="w-10 h-10 fill-[#FFFFFF] bg-success-main rounded-full" />
                    <div
                      className="w-[14.438rem] flex-col justify-start items-top py-0"
                      style={{ lineHeight: '0' }}
                    >
                      <Typography variant="caption" color="text.secondary" className="-pt-10">
                        {dayjs(notification?.created_on).fromNow()}
                      </Typography>
                      <Typography variant="subtitle2" color="#46586B">
                        {notification?.heading}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" className="py-0">
                        {notification?.content.length > 140
                          ? `${notification?.content.substring(0, 140)}...`
                          : notification?.content}
                      </Typography>
                    </div>
                    {!(notification.read || readNotif.includes(notification.id)) && (
                      <div className="mt-1.5">
                        <SingleDotIcon />
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex justify-start items-top gap-2 bg-status-notificationBack my-2 p-2">
                <div className="flex items-center h-inherit">
                  <CommentNotifyIcon className="w-6 h-6 fill-[#FFFFFF] bg-[#2E7D32] rounded-full p-1" />
                </div>
                <div className="flex-col justify-start items-top py-0" style={{ lineHeight: '0' }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="-pt-10"
                  ></Typography>
                  <Typography variant="subtitle2" color="#46586B">
                    No notifications
                  </Typography>
                  <Typography variant="caption" color="text.secondary" className="py-0">
                    You have not received any notifications.
                  </Typography>
                </div>
              </div>
            )}
          </div>
        </Drawer>
      </div>
    </>
  )
}

export default Notification
